import type { BlockRelationship } from '@basehub/data/replicache/index'
import { invariant } from '@epic-web/invariant'
import * as fi from './fractional-indexing-raw'
import { BaseHubTransaction } from '@basehub/data/replicache/tx/iso'

export type Position =
  | { type: 'first' }
  | { type: 'last' }
  | { type: 'before'; targetId: string }
  | { type: 'after'; targetId: string }
  | { type: 'specific'; order: string }

export const generateKeyBetween = (
  a: string | BlockRelationship | null,
  b: string | BlockRelationship | null,
  rootBlockId: string
): string => {
  const aOrder = a ? (typeof a === 'string' ? a : a.order) : null
  const bOrder = b ? (typeof b === 'string' ? b : b.order) : null
  const temp = fi.generateKeyBetween(
    aOrder ?? null,
    bOrder ?? null,
    undefined,
    rootBlockId
  )
  return temp
}

export const generateNKeysBetween = (
  a: string | BlockRelationship | null,
  b: string | BlockRelationship | null,
  length: number,
  rootBlockId: string
): string[] => {
  const aOrder = a ? (typeof a === 'string' ? a : a.order) : null
  const bOrder = b ? (typeof b === 'string' ? b : b.order) : null
  const keys = fi.generateNKeysBetween(
    aOrder ?? null,
    bOrder ?? null,
    length,
    undefined,
    rootBlockId
  )
  return keys
}

export const getOrderFromPosition = async (
  tx: BaseHubTransaction,
  {
    position,
    parentSourceBlockId,
    rootBlockId,
  }: {
    position: Position
    parentSourceBlockId: string
    rootBlockId: string
  }
) => {
  if (position.type === 'specific') return position.order

  const siblings = await tx.getBlockChildren({
    sourceBlockId: parentSourceBlockId,
    rootBlockId,
  })

  switch (position.type) {
    case 'first': {
      const currentFirst = siblings[0]?.order ?? null
      return generateKeyBetween(null, currentFirst, rootBlockId)
    }
    case 'last': {
      const currentLast = siblings[siblings.length - 1]?.order ?? null
      return generateKeyBetween(currentLast, null, rootBlockId)
    }
    case 'before': {
      const targetSiblingIndex = siblings.findIndex(
        (sibling) =>
          sibling.id === position.targetId ||
          sibling.blockId === position.targetId ||
          sibling.sourceBlockId === position.targetId
      )
      invariant(targetSiblingIndex !== -1, 'target sibling not found')
      return generateKeyBetween(
        siblings[targetSiblingIndex - 1]?.order ?? null,
        siblings[targetSiblingIndex]?.order ?? null,
        rootBlockId
      )
    }
    case 'after': {
      const targetSiblingIndex = siblings.findIndex(
        (sibling) =>
          sibling.id === position.targetId ||
          sibling.blockId === position.targetId ||
          sibling.sourceBlockId === position.targetId
      )
      invariant(targetSiblingIndex !== -1, 'target sibling not found')
      const result = generateKeyBetween(
        siblings[targetSiblingIndex]?.order ?? null,
        siblings[targetSiblingIndex + 1]?.order ?? null,
        rootBlockId
      )
      return result
    }
    default:
      position satisfies never
      throw new Error('Invalid position')
  }
}
